import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface IntegrationKey {
  id: string;
  name?: string;
  keyType?: 'qr_integration' | 'confidential_access';
  keyPrefix: string;
  keySuffix: string;
  maskedKey: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
}

export interface IntegrationSubscription {
  active: boolean;
  status?: string | null;
  validUntil?: string | null;
}

export interface IntegrationPricing {
  operationType: string;
  amount: number;
  currency: string;
  formatted: string;
  validityDays: number;
  consentText: string;
  baseRate?: number;
  baseCoverageMonths?: number;
  monthlySurchargePercent?: number;
}

export interface IntegrationAccessReport {
  id: string;
  reportType: string;
  status: string;
  generatedAt: string;
  validUntil: string;
  metadata?: Record<string, any>;
}

export type QrIntegrationKey = IntegrationKey;
export type QrIntegrationSubscription = IntegrationSubscription;
export type QrIntegrationPricing = IntegrationPricing;
export type QrIntegrationAccessReport = IntegrationAccessReport;

export type ConfidentialIntegrationKey = IntegrationKey;
export type ConfidentialIntegrationSubscription = IntegrationSubscription;
export type ConfidentialIntegrationPricing = IntegrationPricing;
export type ConfidentialIntegrationAccessReport = IntegrationAccessReport;

class IntegrationService {
  private getAuthHeader() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  async getQrIntegrationKeys(): Promise<{
    keys: QrIntegrationKey[];
    subscription: QrIntegrationSubscription;
  }> {
    const response = await axios.get(
      `${API_URL}/integrations/qr/keys`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async createQrIntegrationKey(payload?: { name?: string }): Promise<{
    key: string;
    record: QrIntegrationKey;
    subscription: QrIntegrationSubscription;
  }> {
    const response = await axios.post(
      `${API_URL}/integrations/qr/keys`,
      payload || {},
      this.getAuthHeader()
    );
    return response.data;
  }

  async revokeQrIntegrationKey(keyId: string): Promise<{ record: QrIntegrationKey }> {
    const response = await axios.delete(
      `${API_URL}/integrations/qr/keys/${keyId}`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async getQrAccessPricing(): Promise<QrIntegrationPricing> {
    const response = await axios.get(
      `${API_URL}/integrations/qr/access/pricing`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async getQrAccessStatus(): Promise<{ active: boolean; report?: QrIntegrationAccessReport }> {
    const response = await axios.get(
      `${API_URL}/integrations/qr/access`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async purchaseQrAccess(payload: { consentAccepted: boolean }): Promise<{
    report: QrIntegrationAccessReport;
    alreadyActive: boolean;
    pricing: QrIntegrationPricing;
  }> {
    const response = await axios.post(
      `${API_URL}/integrations/qr/access/purchase`,
      payload,
      this.getAuthHeader()
    );
    return response.data;
  }

  async cancelQrAccess(): Promise<{ cancelled: boolean; report?: QrIntegrationAccessReport }> {
    const response = await axios.post(
      `${API_URL}/integrations/qr/access/cancel`,
      {},
      this.getAuthHeader()
    );
    return response.data;
  }

  async getConfidentialKeys(): Promise<{
    keys: ConfidentialIntegrationKey[];
    subscription: ConfidentialIntegrationSubscription;
  }> {
    const response = await axios.get(
      `${API_URL}/integrations/confidential/keys`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async createConfidentialKey(payload?: { name?: string }): Promise<{
    key: string;
    record: ConfidentialIntegrationKey;
    subscription: ConfidentialIntegrationSubscription;
  }> {
    const response = await axios.post(
      `${API_URL}/integrations/confidential/keys`,
      payload || {},
      this.getAuthHeader()
    );
    return response.data;
  }

  async revokeConfidentialKey(keyId: string): Promise<{ record: ConfidentialIntegrationKey }> {
    const response = await axios.delete(
      `${API_URL}/integrations/confidential/keys/${keyId}`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async getConfidentialAccessPricing(): Promise<ConfidentialIntegrationPricing> {
    const response = await axios.get(
      `${API_URL}/integrations/confidential/access/pricing`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async getConfidentialAccessStatus(): Promise<{
    active: boolean;
    report?: ConfidentialIntegrationAccessReport;
  }> {
    const response = await axios.get(
      `${API_URL}/integrations/confidential/access`,
      this.getAuthHeader()
    );
    return response.data;
  }

  async purchaseConfidentialAccess(payload: { consentAccepted: boolean }): Promise<{
    report: ConfidentialIntegrationAccessReport;
    alreadyActive: boolean;
    pricing: ConfidentialIntegrationPricing;
  }> {
    const response = await axios.post(
      `${API_URL}/integrations/confidential/access/purchase`,
      payload,
      this.getAuthHeader()
    );
    return response.data;
  }

  async cancelConfidentialAccess(): Promise<{
    cancelled: boolean;
    report?: ConfidentialIntegrationAccessReport;
  }> {
    const response = await axios.post(
      `${API_URL}/integrations/confidential/access/cancel`,
      {},
      this.getAuthHeader()
    );
    return response.data;
  }
}

export const integrationService = new IntegrationService();
